/**
 * Consent Context
 * 
 * React context for managing cookie consent state across the application.
 * Provides consent state, update methods, and modal controls.
 */

import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import {
  ConsentState,
  ConsentCategory,
  DEFAULT_CONSENT_STATE,
  saveConsent,
  getStoredConsent,
  needsReconsent,
  setDefaultConsentState,
  updateGTMConsent,
  initializeGTMConsent,
  updateMetaConsent,
  logConsentToBackend,
  generateConsentId,
} from '../lib/consent';

interface ConsentContextValue {
  // Current consent state
  consentState: ConsentState;
  
  // Whether user has made a consent decision
  hasDecided: boolean;
  
  // Whether the consent modal is open
  isModalOpen: boolean;
  
  // Update consent for all categories
  updateConsent: (state: ConsentState) => Promise<void>;
  
  // Accept all cookies
  acceptAll: () => Promise<void>;
  
  // Reject all non-necessary cookies
  rejectAll: () => Promise<void>;
  
  // Check if a specific category is consented
  hasConsent: (category: ConsentCategory) => boolean;
  
  // Open/close the consent settings modal
  openModal: () => void;
  closeModal: () => void;
}

const ConsentContext = createContext<ConsentContextValue | null>(null);

interface ConsentProviderProps {
  children: ReactNode;
}

export function ConsentProvider({ children }: ConsentProviderProps) {
  const [consentState, setConsentState] = useState<ConsentState>(DEFAULT_CONSENT_STATE);
  const [hasDecided, setHasDecided] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);

  // Initialize consent state on mount
  useEffect(() => {
    // Set default denied state for GTM BEFORE checking stored consent
    // This ensures "no consent, no fire" even on first load
    setDefaultConsentState();
    
    // Check for existing consent
    const stored = getStoredConsent();
    
    if (stored && !needsReconsent(stored)) {
      // User has valid consent, apply it
      setConsentState(stored.state);
      setHasDecided(true);
      
      // Update GTM and Meta with stored consent
      initializeGTMConsent(stored.state);
      updateMetaConsent(stored.state.marketing);
    } else {
      // No valid consent, keep defaults (all denied except necessary)
      setConsentState(DEFAULT_CONSENT_STATE);
      setHasDecided(false);
    }
    
    setIsInitialized(true);
  }, []);

  // Update consent state and sync with GTM/Meta/Backend
  const updateConsent = useCallback(async (newState: ConsentState) => {
    // Ensure necessary is always true
    const state: ConsentState = {
      ...newState,
      necessary: true,
    };
    
    // Get existing consent ID or generate new one
    const stored = getStoredConsent();
    const consentId = stored?.consentId || generateConsentId();
    
    // Save locally
    saveConsent(state, consentId);
    
    // Update React state
    setConsentState(state);
    setHasDecided(true);
    
    // Update GTM consent signals
    updateGTMConsent(state);
    
    // Update Meta Pixel consent
    updateMetaConsent(state.marketing);
    
    // Log to backend for audit trail (don't await to not block UI)
    logConsentToBackend(consentId, state).catch(err => {
      console.warn('Failed to log consent:', err);
    });
    
    // Close modal if open
    setIsModalOpen(false);
  }, []);

  // Accept all cookies
  const acceptAll = useCallback(async () => {
    await updateConsent({
      necessary: true,
      analytics: true,
      marketing: true,
    });
  }, [updateConsent]);

  // Reject all non-necessary cookies
  const rejectAll = useCallback(async () => {
    await updateConsent({
      necessary: true,
      analytics: false,
      marketing: false,
    });
  }, [updateConsent]);

  // Check if a specific category is consented
  const hasConsent = useCallback((category: ConsentCategory): boolean => {
    if (category === 'necessary') return true;
    return consentState[category] ?? false;
  }, [consentState]);

  // Modal controls
  const openModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const value: ConsentContextValue = {
    consentState,
    hasDecided,
    isModalOpen,
    updateConsent,
    acceptAll,
    rejectAll,
    hasConsent,
    openModal,
    closeModal,
  };

  // Don't render children until initialization is complete
  // This ensures GTM default state is set before any content loads
  if (!isInitialized) {
    return null;
  }

  return (
    <ConsentContext.Provider value={value}>
      {children}
    </ConsentContext.Provider>
  );
}

export function useConsent(): ConsentContextValue {
  const context = useContext(ConsentContext);
  if (!context) {
    throw new Error('useConsent must be used within a ConsentProvider');
  }
  return context;
}

